// frontend/src/pages/dashboard/MainDashboardContent.jsx

import React, { useState } from 'react';
import axios from 'axios';
import { Link, useOutletContext } from 'react-router-dom';
import { TURPE_STAGE_CONFIG } from '../../lib/turpeStageConfig';
import TurpeOptimizationCard from '../../components/TurpeOptimizationCard';
import UploadContainer from '../../components/upload/UploadContainer';

import { 
  FiFileText, FiBarChart2, FiEdit, FiCheckCircle, FiAlertTriangle, 
  FiZap, FiDollarSign, FiTarget, FiTrendingUp, FiTrendingDown, FiBell,
  FiHelpCircle, FiActivity, FiClock, FiUpload, FiDatabase, FiPlus, FiMapPin,
  FiPaperclip, FiFolder, FiAlertCircle, FiPercent
} from 'react-icons/fi';

// 🎯 COMPOSANT PDL QUICK CARD (INCHANGÉ)
const PDLQuickCard = ({ pdl }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'connected':
        return { icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50', text: 'Connecté Enedis' };
      case 'not_connected':
      case 'pending':
        return { icon: FiAlertTriangle, color: 'text-gray-500', bg: 'bg-gray-50', text: 'Non connecté' };
      case 'token_expired':
        return { icon: FiClock, color: 'text-yellow-600', bg: 'bg-yellow-50', text: 'À renouveler' };
      case 'optimized':
        return { icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50', text: 'Optimisé' };
      case 'warning':
        return { icon: FiAlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', text: 'Attention' };
      default:
        return { icon: FiAlertTriangle, color: 'text-red-600', bg: 'bg-red-50', text: 'Erreur' };
    }
  };

  const statusConfig = getStatusConfig(pdl.connection_status || pdl.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-sm">
            {pdl.site_name || pdl.name}
          </h3>
          <p className="text-xs text-gray-500 font-mono">
            {pdl.pdl_number || `Code EIC: ${pdl.eic_code}`}
          </p>
        </div>
        <div className={`p-1 rounded-full ${statusConfig.bg}`}>
          <StatusIcon className={`h-3 w-3 ${statusConfig.color}`} />
        </div>
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
        <span className="flex items-center">
          <FiZap className="h-3 w-3 mr-1" />
          {pdl.contract_power || pdl.subscribed_power} kVA
        </span>
        <span className="flex items-center">
          <FiMapPin className="h-3 w-3 mr-1" />
          {pdl.address?.city || (pdl.voltage_level ? `${pdl.voltage_level}` : 'N/A')}
        </span>
      </div>
      
      <div className="mt-2">
        <span className={`text-xs px-2 py-1 rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
          {statusConfig.text}
        </span>
      </div>

      {pdl.usage_percentage && (
        <div className="mt-2">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Utilisation</span>
            <span>{pdl.usage_percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className={`h-1 rounded-full ${
                pdl.usage_percentage > 90 ? 'bg-red-500' :
                pdl.usage_percentage > 70 ? 'bg-orange-500' :
                'bg-green-500'
              }`}
              style={{ width: `${pdl.usage_percentage}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// 🎯 NOUVEAU COMPOSANT DOCUMENT QUICK CARD
const DocumentQuickCard = ({ document }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'Validé':
        return { 
          icon: FiCheckCircle, 
          color: 'text-green-600', 
          bg: 'bg-green-50', 
          text: 'Validé',
          border: 'border-green-200'
        };
      case 'Fourni':
      case 'En attente':
        return { 
          icon: FiClock, 
          color: 'text-blue-600', 
          bg: 'bg-blue-50', 
          text: 'En attente',
          border: 'border-blue-200'
        };
      case 'Rejeté':
        return { 
          icon: FiAlertTriangle, 
          color: 'text-red-600', 
          bg: 'bg-red-50', 
          text: 'Rejeté',
          border: 'border-red-200'
        };
      case 'Optionnel':
        return { 
          icon: FiHelpCircle, 
          color: 'text-gray-500', 
          bg: 'bg-gray-50', 
          text: 'Optionnel',
          border: 'border-gray-200'
        };
      default:
        return { 
          icon: FiAlertCircle, 
          color: 'text-orange-600', 
          bg: 'bg-orange-50', 
          text: 'À fournir',
          border: 'border-orange-200'
        };
    }
  };

  const statusConfig = getStatusConfig(document.status);
  const StatusIcon = statusConfig.icon;
  const fileCount = document.files?.length || 0;
  const hasFiles = fileCount > 0;

  return (
    <div className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${statusConfig.border}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-sm mb-1">
            {document.name}
          </h3>
          <p className="text-xs text-gray-500">
            {document.priority === 1 ? 'Priorité élevée' : 
             document.priority === 2 ? 'Important' : 
             'Optionnel'}
            {document.estimatedSavings && (
              <span className="ml-2 text-green-600 font-medium">
                • {document.estimatedSavings} d'économies
              </span>
            )}
          </p>
        </div>
        <div className={`p-1 rounded-full ${statusConfig.bg}`}>
          <StatusIcon className={`h-3 w-3 ${statusConfig.color}`} />
        </div>
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
        <span className="flex items-center">
          <FiPaperclip className="h-3 w-3 mr-1" />
          {fileCount} fichier{fileCount !== 1 ? 's' : ''}
        </span>
        {document.maxFileSizeMB && (
          <span className="flex items-center">
            <FiFolder className="h-3 w-3 mr-1" />
            Max {document.maxFileSizeMB}MB
          </span>
        )}
      </div>
      
      <div className="mt-2 mb-2">
        <span className={`text-xs px-2 py-1 rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
          {statusConfig.text}
        </span>
      </div>

      {/* Barre de progression pour les documents avec périodes */}
      {document.requiresPeriods && hasFiles && (
        <div className="mt-2">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Completion</span>
            <span>{document.completionScore || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className={`h-1 rounded-full ${
                (document.completionScore || 0) >= 80 ? 'bg-green-500' :
                (document.completionScore || 0) >= 50 ? 'bg-blue-500' :
                'bg-orange-500'
              }`}
              style={{ width: `${document.completionScore || 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Description courte */}
      <div className="mt-2 text-xs text-gray-500">
        {document.description?.[0]?.substring(0, 80)}...
      </div>
    </div>
  );
};

const MainDashboardContent = () => {
  // --- Données et États ---
  const { 
    userProfile: profile, 
    deliveryPoints, 
    alerts, 
    pdls, 
    pdlStats, 
    onRefreshPDL,
    requiredDocuments = [], // 🎯 AJOUT DES DOCUMENTS
    documentsCompletionRate = 0, // 🎯 AJOUT TAUX DE COMPLETION
    totalPotentialSavings = 0
  } = useOutletContext() || {};
  
  // États pour la section de test de communication
  const [calculationResult, setCalculationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // États pour l'upload
  const [showUpload, setShowUpload] = useState(false);
  const [loadCurveData, setLoadCurveData] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('pending');

  // Logique pour déterminer quelles données afficher
  const displayPDLs = pdls && pdls.length > 0 ? pdls : deliveryPoints;
  const hasPDLData = pdls && pdls.length > 0;
  const hasDocumentData = requiredDocuments && requiredDocuments.length > 0;

  // 🎯 CALCULS POUR LES DOCUMENTS
  const documentsStats = {
    total: requiredDocuments.length,
    completed: requiredDocuments.filter(d => ['Validé', 'Fourni', 'En attente'].includes(d.status)).length,
    validated: requiredDocuments.filter(d => d.status === 'Validé').length,
    missing: requiredDocuments.filter(d => d.status === 'À fournir').length,
    rejected: requiredDocuments.filter(d => d.status === 'Rejeté').length
  };

  // --- Logique d'Upload (INCHANGÉE) ---
  const handleUploadSuccess = (validationResult) => {
    setLoadCurveData(validationResult);
    setShowUpload(false);
    setUploadStatus('loaded');
  };

  // --- Logique de Calcul (INCHANGÉE) ---
  const handleCalculateClick = async () => {
    setIsLoading(true);
    setError(null);
    setCalculationResult(null);

    const requestData = loadCurveData ? {
      loadCurve: {
        points: loadCurveData.data_preview || [
          { timestamp: "2024-01-15T10:00:00Z", puissance_active_kw: 150.5 },
          { timestamp: "2024-01-15T10:30:00Z", puissance_active_kw: 155.2 },
          { timestamp: "2024-01-15T11:00:00Z", puissance_active_kw: 160.0 },
        ],
        metadata: {
          source: "UPLOAD",
          filename: loadCurveData.statistics?.filename || "test-data.csv",
          total_points: loadCurveData.statistics?.total_points || 3
        }
      },
      contractData: {
        power_subscriptions: { 
          hph: 200, hch: 200, hpb: 200, hcb: 200 
        },
        tension_domain: "BT_SUP_36",
        utilisation_version: "CU",
        metadata: {
          contract_id: "CTR-TEST-001",
          pdl: "12345678901234",
          contract_start_date: "2024-01-01"
        }
      }
    } : {
      loadCurve: {
        points: [
          { timestamp: "2024-01-15T10:00:00Z", puissance_active_kw: 150.5 },
          { timestamp: "2024-01-15T10:30:00Z", puissance_active_kw: 155.2 },
          { timestamp: "2024-01-15T11:00:00Z", puissance_active_kw: 160.0 },
        ]
      },
      contractData: {
        power_subscriptions: { hph: 200, hch: 200, hpb: 200, hcb: 200 },
        tension_domain: "BT_SUP_36",
        utilisation_version: "CU",
        metadata: {
          contract_id: "CTR-TEST-001",
          pdl: "12345678901234",
          contract_start_date: "2024-01-01"
        }
      }
    };

    try {
      const response = await axios.post('/api/v3/calculate', requestData);
      setCalculationResult(response.data);
      setUploadStatus('processed');
    } catch (err) {
      console.error('Erreur calcul TURPE:', err);
      setError(err.response ? err.response.data.message : "Erreur de communication avec le serveur.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Valeurs par défaut pour l'affichage
  const stepKey = profile?.optimization_stage || 'onboarding';
  const configForCurrentStep = TURPE_STAGE_CONFIG[stepKey] || TURPE_STAGE_CONFIG.default;
  const progressBarWidthPercentage = profile?.optimization_progress_percentage ?? configForCurrentStep.baseProgression;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec Action Upload */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Optimisation TURPE
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Suivez l'optimisation de vos coûts de transport d'électricité
              </p>
            </div>
            
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 font-medium"
            >
              <FiUpload className="mr-2 h-5 w-5" />
              {loadCurveData ? 'Changer courbe de charge' : 'Importer courbe de charge'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Section données uploadées (INCHANGÉE) */}
        {loadCurveData && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-green-100 rounded-full p-2 mr-3">
                  <FiCheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900">
                    ✨ Données chargées avec succès !
                  </h3>
                  <p className="text-sm text-green-700">
                    Fichier: {loadCurveData.statistics.filename} | 
                    {' '}{loadCurveData.statistics.total_points.toLocaleString()} points | 
                    {' '}Qualité: {loadCurveData.quality_report.data_completeness.toFixed(1)}%
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowUpload(true)}
                  className="text-sm px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  Modifier
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white rounded-lg border border-green-200">
                <div className="text-xl font-bold text-green-600">
                  {loadCurveData.statistics.puissance_max_kw.toFixed(0)}
                </div>
                <div className="text-xs text-green-600">Puissance max (kW)</div>
              </div>
              
              <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                <div className="text-xl font-bold text-blue-600">
                  {(loadCurveData.statistics.energie_totale_kwh / 1000).toFixed(0)}
                </div>
                <div className="text-xs text-blue-600">Énergie (MWh)</div>
              </div>
              
              <div className="text-center p-3 bg-white rounded-lg border border-purple-200">
                <div className="text-xl font-bold text-purple-600">
                  {Math.ceil((new Date(loadCurveData.statistics.end_date) - new Date(loadCurveData.statistics.start_date)) / (1000 * 60 * 60 * 24))}
                </div>
                <div className="text-xs text-purple-600">Jours analysés</div>
              </div>
              
              <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
                <div className="text-xl font-bold text-orange-600">
                  {loadCurveData.quality_report.gaps_detected.length + loadCurveData.quality_report.outliers_detected.length}
                </div>
                <div className="text-xs text-orange-600">Anomalies détectées</div>
              </div>
            </div>
          </div>
        )}

        {/* Progression (INCHANGÉE) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Progression</h2>
              <p className="text-sm text-gray-600">
                Étape actuelle : <span className="font-semibold text-blue-600">{configForCurrentStep.label}</span>
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{progressBarWidthPercentage}%</div>
              <div className="text-sm text-gray-500">Complété</div>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="h-3 rounded-full bg-blue-500 transition-all duration-700"
              style={{ width: `${progressBarWidthPercentage}%` }}
            />
          </div>
        </div>

        {/* 🎯 STATISTIQUES MISES À JOUR AVEC DOCUMENTS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Coût TURPE Actuel</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {calculationResult?.result?.cout_total_annuel ? 
                    `${Math.round(calculationResult.result.cout_total_annuel)}€` : 
                    '19 700€'
                  }
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <FiDollarSign className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Points de Livraison</h3>
                <p className="text-2xl font-bold text-gray-900">{displayPDLs?.length || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FiZap className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Documents Fournis</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {documentsStats.completed}/{documentsStats.total}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FiFileText className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Économies Potentielles</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {totalPotentialSavings > 0 ? `${totalPotentialSavings}€` : '3 150€'}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FiTrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 🎯 SECTION DOCUMENTS (NOUVELLE) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Documents Requis</h2>
              <p className="text-sm text-gray-600">
                {hasDocumentData ? (
                  <>
                    {documentsStats.completed}/{documentsStats.total} documents fournis
                    {documentsCompletionRate > 0 && (
                      <span className="ml-2 text-blue-600 font-medium">
                        • {documentsCompletionRate}% complété
                      </span>
                    )}
                  </>
                ) : (
                  'Téléversez vos documents pour une analyse TURPE optimale'
                )}
              </p>
            </div>
            <Link 
              to="/dashboard/documents"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiFileText className="mr-2 h-4 w-4" />
              {documentsStats.total > 0 ? 'Gérer les documents' : 'Ajouter des documents'}
            </Link>
          </div>

          {hasDocumentData && requiredDocuments.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {requiredDocuments.slice(0, 3).map((document) => (
                  <DocumentQuickCard key={document.id} document={document} />
                ))}
              </div>
              
              {/* Métriques rapides documents */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{documentsStats.validated}</div>
                  <div className="text-xs text-green-600">Validés</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">{documentsStats.completed - documentsStats.validated}</div>
                  <div className="text-xs text-blue-600">En attente</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-lg font-bold text-orange-600">{documentsStats.missing}</div>
                  <div className="text-xs text-orange-600">Manquants</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-lg font-bold text-purple-600">{documentsCompletionRate}%</div>
                  <div className="text-xs text-purple-600">Progression</div>
                </div>
              </div>
              
              {requiredDocuments.length > 3 && (
                <div className="text-center pt-4 border-t">
                  <Link 
                    to="/dashboard/documents"
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Voir tous les documents →
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <FiFileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Téléversez vos documents
              </h3>
              <p className="text-gray-500 mb-4">
                Factures d'électricité et contrats nécessaires pour l'optimisation TURPE
              </p>
              <Link 
                to="/dashboard/documents"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all duration-200"
              >
                <FiPlus className="mr-2 h-5 w-5" />
                Commencer l'upload
              </Link>
            </div>
          )}
        </div>

        {/* 🎯 SECTION PDL INTÉGRÉE (INCHANGÉE) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Points de Livraison</h2>
              <p className="text-sm text-gray-600">
                {hasPDLData ? 
                  `${pdlStats?.connected || 0}/${pdlStats?.total || 0} PDL connectés à Enedis` :
                  'Vos PDL connectés à Enedis pour l\'optimisation automatique'
                }
              </p>
            </div>
            <Link 
              to="/dashboard/pdl"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiZap className="mr-2 h-4 w-4" />
              {hasPDLData ? 'Gérer les PDL' : 'Ajouter des PDL'}
            </Link>
          </div>

          {displayPDLs && displayPDLs.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {displayPDLs.slice(0, 3).map((pdl) => (
                  <PDLQuickCard key={pdl.id} pdl={pdl} />
                ))}
              </div>
              
              {displayPDLs.length > 3 && (
                <div className="text-center pt-4 border-t">
                  <Link 
                    to="/dashboard/pdl"
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Voir tous les {displayPDLs.length} PDL →
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <FiZap className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Connectez vos premiers PDL
              </h3>
              <p className="text-gray-500 mb-4">
                Commencez l'optimisation en connectant vos Points de Livraison à Enedis
              </p>
              <Link 
                to="/dashboard/pdl"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all duration-200"
              >
                <FiPlus className="mr-2 h-5 w-5" />
                Ajouter mon premier PDL
              </Link>
            </div>
          )}
        </div>

        {/* Optimisation TURPE (INCHANGÉE) */}
        <div className="mb-8">
          <TurpeOptimizationCard userProfile={profile} />
        </div>
        
        {/* Section de Test de Communication (INCHANGÉE) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Moteur de Calcul TURPE</h2>
              <p className="text-sm text-gray-600">
                {loadCurveData ? 
                  'Testez le calcul avec vos données uploadées' : 
                  'Test de communication Frontend ➡️ Backend ➡️ Moteur Python'
                }
              </p>
            </div>
            
            {loadCurveData && (
              <div className="text-right">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <FiDatabase className="w-3 h-3 mr-1" />
                  Données prêtes
                </span>
              </div>
            )}
          </div>

          <button 
            onClick={handleCalculateClick}
            disabled={isLoading}
            className={`inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:bg-gray-400 ${
              loadCurveData ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-offset-2`}
          >
            <FiZap className="mr-2 h-4 w-4" />
            {isLoading ? "Calcul en cours..." : 
             loadCurveData ? "Calculer avec mes données" : 
             "Lancer le Calcul de Test"}
          </button>

          {error && (
            <div className="mt-4 text-red-600 bg-red-100 p-3 rounded-lg border border-red-200">
              <div className="flex items-center">
                <FiAlertTriangle className="w-4 h-4 mr-2" />
                {error}
              </div>
            </div>
          )}
          
          {calculationResult && (
            <div className="mt-4 bg-gray-800 text-white p-4 rounded-lg font-mono text-xs overflow-x-auto">
              <h3 className="font-semibold mb-2 text-green-400 flex items-center">
                <FiCheckCircle className="w-4 h-4 mr-2" />
                ✅ Réponse reçue du Moteur Python :
              </h3>
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(calculationResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Modal d'upload (INCHANGÉE) */}
        {showUpload && (
          <UploadContainer 
            onUploadSuccess={handleUploadSuccess}
            onClose={() => setShowUpload(false)}
          />
        )}

      </div>
    </div>
  );
};

export default MainDashboardContent;
